import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Route as RouteIcon,
  Users,
  Contact,
  Building2,
  Bus,
  UserCog,
  FileSignature,
  Radio,
  Receipt,
  Wrench,
  FolderOpen,
  BarChart3,
  Settings,
  LogOut,
  KeyRound,
  ChevronDown,
  ChevronRight,
  UserCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { GlobalSearch } from "@/components/GlobalSearch";
import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle, ThemeSegmented } from "@/components/ThemeToggle";
import { LocalTimeIndicator } from "@/components/LocalTimeIndicator";
import { QuickAdd } from "@/components/QuickAdd";
import NotificationBell from "@/components/notifications/NotificationBell";
import { SearchFocusProvider, useSearchFocus } from "@/contexts/SearchFocusContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// RC10h · Round 5 — single canonical active-navigation resolver. Both
// the desktop sidebar AND the mobile bottom-nav / More sheet import
// `resolveActiveNavKey` from here. Previously each surface computed
// active-state independently using a broad `startsWith(item.to + "/")`
// test; because `/fleet/asset-owners` has `/fleet` as a prefix, this
// co-highlighted BOTH Fleet AND Asset Owners. The resolver uses
// longest-match-wins ordering so nested routes always win over their
// parent prefix.
import { resolveActiveNavKey } from "@/lib/navActive";

const NAV_GROUPS = [
  { heading: "Operations", items: [
    { to: "/dashboard", navKey: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    // Correction Batch STEP 2 — top-level Tours nav lands on the
    // Schedule view first, ALWAYS. `sessionStorage[aa_tours_view_pref]`
    // MUST NOT override this. Schedule already exists as a distinct
    // route at `/schedule` (see `pages/schedule/SchedulePage.jsx`); per
    // Owner: use as-is. The Tours page continues to host Timeline+List
    // as sibling views — sub-view context IS preserved on browser Back
    // (URL echo in ToursList). Both desktop + mobile use the same
    // `navToSchedule` handler below.
    { to: "/tours", navTarget: "/schedule", navKey: "tours", label: "Tours", icon: RouteIcon, permission: "tours.view" },
    { to: "/dispatch", navKey: "dispatch", label: "Dispatch", icon: Radio, permission: "dispatch.view" },
  ]},
  { heading: "Relationships", items: [
    { to: "/customers", navKey: "clients", label: "Clients", icon: Users, permission: "customers.view" },
    // RC4 · Organizations sidebar entry removed; the Clients (customers)
    // directory is now the single destination. `/organizations` and
    // `/organizations/:id` are redirected to `/clients` (see App.js).
    { to: "/contacts", navKey: "people", label: "People", icon: Contact, permission: "contacts.view" },
    // RC4 · Organizations moved: consolidated into Clients (see /organizations
    // → /clients redirects in App.js).
  ]},
  { heading: "Resources", items: [
    { to: "/fleet", navKey: "fleet", label: "Fleet", icon: Bus, permission: "fleet.view" },
    { to: "/fleet/asset-owners", navKey: "asset-owners", label: "Asset Owners", icon: Bus },
    { to: "/drivers", navKey: "drivers", label: "Drivers", icon: UserCog, permission: "drivers.view" },
    { to: "/service", navKey: "service", label: "Service", icon: Wrench, permission: "service.view" },
  ]},
  { heading: "Commercial", items: [
    { to: "/quotes", navKey: "quotes", label: "Quotes & Contracts", icon: FileSignature, permission: "quotes.view" },
    { to: "/billing", navKey: "billing", label: "Billing", icon: Receipt, permission: "billing.view" },
    { to: "/documents", navKey: "documents", label: "Documents", icon: FolderOpen },
  ]},
  { heading: "Intelligence", items: [
    { to: "/reports", navKey: "reports", label: "Reports", icon: BarChart3, permission: "reports.operational" },
  ]},
];

const ADMIN_SUBNAV = [
  { to: "/administration/users", label: "Users & Access", adminOnly: true },
  { to: "/administration/roles", label: "Roles & Permissions", adminOnly: true },
  { to: "/administration/quote-rates", label: "Quote Rates", adminOnly: true },
  { to: "/administration/fleet-settings/unit-types", label: "Fleet Settings · Unit Types", superAdminOnly: true },
  { to: "/administration/integrations", label: "Integrations", adminOnly: true },
  { to: "/administration/imports/legacy", label: "Legacy Import", adminOnly: true },
  { to: "/administration/settings", label: "System Settings", adminOnly: true },
  // RC3 · super_admin only; Owner-controlled scheduler for tour archiving.
  { to: "/administration/auto-archive", label: "Auto-Archive", superAdminOnly: true },
  // F7C Purge nav item removed at Final Handoff Gate (client-delivery-rc1)
  { to: "/administration/audit-log", label: "Audit Log", superAdminOnly: true },
];

// Nav-visibility resolver — one function, four consumers. Filters
// static item arrays using the CANONICAL access model from
// AuthContext (`can(permKey)` / `isAdmin` / `isSuperAdmin`). This is
// NOT a new visibility system: it is the same permission catalog that
// gates page-level RoleGates and API-level `require_permission(...)`.
// A hidden nav item ONLY hides the visual entry — server-side authz
// remains authoritative. See `services/authz.py` for the mirror.
//
// Safe path: nav items with NO gating metadata (e.g. Dashboard,
// Documents) are always visible so a mis-provisioned user never lands
// on an empty shell. The Dashboard route itself has no server-side
// permission gate; every internal user reaches it.
const isNavItemVisible = (item, gate) => {
  if (!item) return false;
  if (item.superAdminOnly) return !!gate.isSuperAdmin;
  if (item.adminOnly) return !!gate.isAdmin;
  if (item.permission) return !!gate.can?.(item.permission);
  return true;
};

const visibleItems = (items, gate) => (items || []).filter((it) => isNavItemVisible(it, gate));

export const Layout = () => (
  <SearchFocusProvider>
    <LayoutInner />
  </SearchFocusProvider>
);

const LayoutInner = () => {
  const { user, logout, isAdmin, isSuperAdmin, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [adminOpen, setAdminOpen] = useState(loc.pathname.startsWith("/administration"));
  const { focus: focusSearch } = useSearchFocus();
  const gate = React.useMemo(() => ({ can, isAdmin, isSuperAdmin }), [can, isAdmin, isSuperAdmin]);
  // RC10h · Round 5 — single canonical active-navigation key for the
  // current pathname. The desktop sidebar AND the mobile shell BOTH
  // consume the same resolver so that exactly one top-level nav item
  // is ever highlighted at a time. See `frontend/src/lib/navActive.js`.
  const activeNavKey = React.useMemo(
    () => resolveActiveNavKey(loc.pathname),
    [loc.pathname],
  );
  // Priority-0 fix: filter every static nav array through the shared
  // permission gate. `visibleItems` never returns items that are
  // supposed to be hidden by role/permission. Dashboard has no
  // permission key and therefore always survives the filter — this
  // is the fallback-safe path the P0 brief requires.
  const visibleGroups = React.useMemo(
    () => NAV_GROUPS.map((g) => ({ ...g, items: visibleItems(g.items, gate) })).filter((g) => g.items.length > 0),
    [gate],
  );
  const adminSubnav = React.useMemo(() => visibleItems(ADMIN_SUBNAV, gate), [gate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/") return;
      const el = document.activeElement;
      const tag = el?.tagName;
      const isEditable = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable;
      if (isEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      focusSearch();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [focusSearch]);

  const onLogout = async () => { await logout(); nav("/login"); };
  const [changePwOpen, setChangePwOpen] = React.useState(false);

  const initials = (user?.name || "?")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-canvas)" }}>
      {/* Desktop sidebar */}
      <aside
        data-testid="left-nav"
        className="aa-nav-bg hidden md:flex w-64 shrink-0 text-[color:var(--fg-secondary)] flex-col"
        style={{ borderRight: "1px solid var(--border-subtle)" }}
      >
        <div
          className="flex items-center justify-center px-3"
          style={{ height: 88, borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <BrandLogo forceDark fluid withOpsMarker={false} testid="left-nav-brand" />
        </div>

        <nav className="flex-1 py-2 px-2 overflow-y-auto">
          {visibleGroups.map((group, gi) => (
            <div key={group.heading} className={gi > 0 ? "mt-1 pt-1" : ""} style={gi > 0 ? { borderTop: "1px solid rgba(255,255,255,0.05)" } : {}}>
              <div className="aa-nav-section" data-testid={`nav-group-${group.heading.toLowerCase()}`}>
                {group.heading}
              </div>
              {group.items.map((item) => {
                const isActive = item.navKey === activeNavKey;
                return (
                <NavLink
                  key={item.to}
                  to={item.navTarget || item.to}
                  end
                  data-testid={`nav-${item.to.replace(/^\//, "").replaceAll("/", "-")}`}
                  className="aa-nav-item"
                  data-active={isActive ? "true" : "false"}
                  aria-current={isActive ? "page" : undefined}
                  onClick={(e) => {
                    // Correction Batch STEP 2 — top-level nav clicks
                    // FORCE-navigate to `navTarget` (Schedule for Tours)
                    // even if `to` matches the current pathname. Without
                    // this, react-router treats a same-path <NavLink>
                    // click as a no-op and users stuck on Timeline never
                    // return to Schedule. Modifier-key clicks (Cmd/Ctrl/
                    // middle-click open-in-new-tab) fall through to the
                    // browser's default anchor behavior with `navTarget`
                    // as the href — desired for both desktop and mobile.
                    if (!item.navTarget) return;
                    if (e.defaultPrevented) return;
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                    e.preventDefault();
                    nav(item.navTarget);
                  }}
                >
                  <item.icon size={20} className="opacity-80" />
                  <span>{item.label}</span>
                </NavLink>
                );
              })}
            </div>
          ))}

          {/* System group — hidden entirely for non-admins so the
              Administration expander doesn't show a taunt-empty tree. */}
          {isAdmin && adminSubnav.length > 0 && (
          <div className="mt-1 pt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <div className="aa-nav-section" data-testid="nav-group-system">System</div>
            <button
              type="button"
              data-testid="nav-administration"
              onClick={() => setAdminOpen((v) => !v)}
              className="aa-nav-item w-full text-left"
              data-active={loc.pathname.startsWith("/administration") ? "true" : "false"}
            >
              <Settings size={20} className="opacity-80" />
              <span className="flex-1">Administration</span>
              {adminOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {adminOpen && (
              <div className="ml-6 mt-1 mb-2" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
                {adminSubnav.map((s) => (
                  <NavLink
                    key={s.to}
                    to={s.to}
                    data-testid={`nav-admin-${s.to.split("/").pop()}`}
                    className="block px-3 py-1.5 text-[12.5px] hover:text-white"
                    style={({ isActive }) => ({
                      color: isActive ? "#ffffff" : "#B8BFC5",
                      borderLeft: isActive ? "2px solid var(--brand-lime)" : "2px solid transparent",
                      marginLeft: -1,
                      paddingLeft: 11,
                    })}
                  >
                    {s.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          )}
        </nav>

        {/* Sidebar footer — user identity chip */}
        <div className="px-3 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="flex items-center gap-2 px-1">
            <div className="w-8 h-8 rounded-md bg-[color:var(--brand-blue)] text-white grid place-items-center text-[11px] font-semibold">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-white truncate">{user?.name}</div>
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--fg-muted)]">
                {user?.role?.replaceAll("_", " ")}
              </div>
            </div>
            <button
              onClick={onLogout}
              title="Sign out"
              data-testid="sidebar-logout"
              className="w-8 h-8 grid place-items-center rounded-md text-[color:var(--fg-muted)] hover:text-white hover:bg-white/[0.06]"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Right side */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          data-testid="top-bar"
          className="h-14 flex items-center px-3 md:px-6 gap-2 md:gap-3"
          style={{
            background: "var(--bg-surface)",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <div className="flex-1 min-w-0 max-w-xl">
            <GlobalSearch />
          </div>

          <LocalTimeIndicator />

          <QuickAdd compact />

          <ThemeToggle compact />

          {user && <NotificationBell />}

          {user && (
            <div className="hidden sm:block">
              <RoleBadge role={user.role} />
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 text-[13px] shrink-0"
                style={{ color: "var(--fg-primary)" }}
              >
                <div className="w-8 h-8 rounded-md bg-[color:var(--brand-blue)] text-white grid place-items-center text-[11px] font-semibold">
                  {initials}
                </div>
                <span className="hidden md:inline">{user?.name}</span>
                <ChevronDown size={14} className="hidden md:inline" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">Signed in as</div>
                <div className="text-[13px] text-[color:var(--fg-primary)] mt-0.5">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-2 flex items-center justify-between text-[12.5px] text-[color:var(--fg-secondary)]">
                <span>Theme</span>
                <ThemeSegmented />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-change-password"
                                onClick={() => setChangePwOpen(true)}
                                className="cursor-pointer">
                <KeyRound size={14} className="mr-2" />
                Change my password
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-logout" onClick={onLogout} className="cursor-pointer">
                <LogOut size={14} className="mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 aa-surface overflow-y-auto overflow-x-hidden pb-20 md:pb-0">
          <Outlet />
        </main>
        <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
      </div>
      <MobileBottomNav />
    </div>
  );
};

const MOBILE_PRIMARY = [
  { to: "/dashboard", navKey: "dashboard", label: "Home", icon: LayoutDashboard },
  // Correction Batch STEP 2 — mobile Tours nav also lands on Schedule.
  { to: "/tours", navTarget: "/schedule", navKey: "tours", label: "Tours", icon: RouteIcon, permission: "tours.view" },
  { to: "/dispatch", navKey: "dispatch", label: "Dispatch", icon: Radio, permission: "dispatch.view" },
  { to: "/fleet", navKey: "fleet", label: "Fleet", icon: Bus, permission: "fleet.view" },
];

const MOBILE_MORE = [
  { to: "/customers", navKey: "clients", label: "Clients", icon: Users, permission: "customers.view" },
  { to: "/contacts", navKey: "people", label: "People", icon: Contact, permission: "contacts.view" },
  { to: "/fleet/asset-owners", navKey: "asset-owners", label: "Asset Owners", icon: Bus },
  { to: "/drivers", navKey: "drivers", label: "Drivers", icon: UserCog, permission: "drivers.view" },
  { to: "/service", navKey: "service", label: "Service", icon: Wrench, permission: "service.view" },
  { to: "/quotes", navKey: "quotes", label: "Quotes & Contracts", icon: FileSignature, permission: "quotes.view" },
  { to: "/billing", navKey: "billing", label: "Billing", icon: Receipt, permission: "billing.view" },
  { to: "/documents", navKey: "documents", label: "Documents", icon: FolderOpen },
  { to: "/reports", navKey: "reports", label: "Reports", icon: BarChart3, permission: "reports.operational" },
  { to: "/schedule", navKey: "tours", label: "Schedule", icon: Calendar, permission: "calendar.view" },
  { to: "/administration/integrations", navKey: null, label: "Integrations", icon: Settings, adminOnly: true },
];

const MobileBottomNav = () => {
  const { user, logout, isAdmin, isSuperAdmin, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const gate = React.useMemo(() => ({ can, isAdmin, isSuperAdmin }), [can, isAdmin, isSuperAdmin]);
  // RC10h · Round 5 — same canonical resolver as the desktop sidebar.
  // Guarantees that the mobile bottom-nav AND the mobile More sheet
  // highlight the same single nav entry the desktop sidebar does.
  const activeNavKey = React.useMemo(
    () => resolveActiveNavKey(loc.pathname),
    [loc.pathname],
  );
  // Same permission gate as the desktop sidebar. Guarantees the two
  // surfaces agree; a role that hides a tab on desktop hides the same
  // tab on the mobile bottom-nav and the More sheet.
  const mobilePrimary = React.useMemo(() => visibleItems(MOBILE_PRIMARY, gate), [gate]);
  const mobileMore = React.useMemo(() => visibleItems(MOBILE_MORE, gate), [gate]);
  return (
    <>
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-20 flex justify-around items-stretch pb-[env(safe-area-inset-bottom)]"
        style={{
          background: "var(--bg-elevated)",
          borderTop: "1px solid var(--border-default)",
        }}
      >
        {mobilePrimary.map((item) => {
          const isActive = item.navKey === activeNavKey;
          return (
          <NavLink
            key={item.to}
            to={item.navTarget || item.to}
            end
            data-testid={`mobile-nav-${item.to.replace(/^\//, "").replaceAll("/", "-")}`}
            className="flex-1 flex flex-col items-center justify-center py-2 text-[10px] gap-1 min-h-[56px] relative"
            data-active={isActive ? "true" : "false"}
            aria-current={isActive ? "page" : undefined}
            onClick={(e) => {
              // Mirror of desktop-nav force-navigation for STEP 2. See
              // Layout.jsx sidebar handler for the rationale.
              if (!item.navTarget) return;
              if (e.defaultPrevented) return;
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
              e.preventDefault();
              nav(item.navTarget);
            }}
          >
            {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[3px] rounded-b-sm" style={{ background: "var(--brand-lime)" }} />}
            <item.icon size={20} className={isActive ? "text-[color:var(--brand-lime)]" : "text-[color:var(--fg-muted)]"} />
            <span className={isActive ? "text-[color:var(--brand-lime)] font-medium" : "text-[color:var(--fg-muted)]"}>{item.label}</span>
          </NavLink>
          );
        })}
        <button
          onClick={() => setMoreOpen(true)}
          data-testid="mobile-nav-more"
          className="flex-1 flex flex-col items-center justify-center py-2 text-[10px] gap-1 min-h-[56px] text-[color:var(--fg-muted)]"
        >
          <Settings size={20} />
          <span>More</span>
        </button>
      </nav>
      {moreOpen && (
        <div
          data-testid="mobile-more-sheet"
          className="md:hidden fixed inset-0 z-30 overflow-y-auto aa-nav-bg text-white"
        >
          <div
            className="flex items-center justify-between gap-3 px-3"
            style={{ height: 72, borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex-1 min-w-0" style={{ height: 56 }}>
              <BrandLogo forceDark fluid withOpsMarker={false} />
            </div>
            <button onClick={() => setMoreOpen(false)} className="shrink-0 text-[color:var(--fg-secondary)] text-[13px]" data-testid="mobile-more-close">
              Close
            </button>
          </div>
          <div className="aa-nav-section">Menu</div>
          <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
            <QuickAdd />
          </div>
          <ul>
            {mobileMore.map((m) => {
              const isActive = !!m.navKey && m.navKey === activeNavKey;
              return (
              <li key={m.to}>
                <NavLink
                  to={m.to}
                  end
                  onClick={() => setMoreOpen(false)}
                  data-testid={`mobile-more-${m.to.replace(/^\//, "").replaceAll("/", "-")}`}
                  className="flex items-center gap-3 px-4 py-3 text-[14px]"
                  data-active={isActive ? "true" : "false"}
                  aria-current={isActive ? "page" : undefined}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", color: isActive ? "#ffffff" : "#F1F3F4", background: isActive ? "rgba(183,227,29,0.08)" : "transparent" }}
                >
                  <m.icon size={18} className={isActive ? "text-[color:var(--brand-lime)]" : "text-[color:var(--fg-muted)]"} />
                  {m.label}
                </NavLink>
              </li>
              );
            })}
          </ul>
          <div className="aa-nav-section mt-3">Settings</div>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
            <span className="text-[14px]">Theme</span>
            <ThemeSegmented />
          </div>
          <div className="p-4">
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="mobile-signout"
              className="w-full h-10 rounded-md bg-white/[0.06] text-white text-[13px] flex items-center justify-center gap-2"
            >
              <LogOut size={14} /> Sign out
            </button>
            <div className="mt-3 flex items-center gap-2 text-[color:var(--fg-muted)] text-[12px]">
              <UserCircle2 size={14} /> {user?.email}
            </div>
          </div>
        </div>
      )}
    </>
  );
};


// F2B · Self-serve change password dialog. Requires the current
// password + new + confirm. Reuses POST /auth/change-password.
const ChangePasswordDialog = ({ open, onClose }) => {
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setCurrent(""); setNext(""); setConfirm(""); setErr(null); setShow(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (next.length < 8) return setErr("New password must be at least 8 characters.");
    if (next !== confirm) return setErr("New password fields do not match.");
    if (next === current) return setErr("New password must differ from the current password.");
    setBusy(true);
    try {
      const { api } = await import("@/lib/api");
      const { toast: t } = await import("sonner");
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      t.success("Password updated");
      onClose();
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
         data-testid="change-password-dialog">
      <div className="bg-white border border-coach-silver rounded-md shadow-xl max-w-md w-full">
        <div className="bg-carbon text-white px-5 py-3 text-[12px] uppercase tracking-widest">
          Change your password
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-[13px]">
          {[
            ["Current password", current, setCurrent, "cp-current"],
            ["New password", next, setNext, "cp-new"],
            ["Confirm new password", confirm, setConfirm, "cp-confirm"],
          ].map(([label, val, setVal, tid]) => (
            <label key={tid} className="block">
              <div className="text-[11px] uppercase tracking-widest text-steel mb-1">{label}</div>
              <div className="relative">
                <input type={show ? "text" : "password"} value={val}
                       onChange={(e) => setVal(e.target.value)} required
                       data-testid={tid}
                       className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 pr-16" />
                <button type="button" onClick={() => setShow((s) => !s)}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[11px] text-coach-blue">
                  {show ? "Hide" : "Show"}
                </button>
              </div>
            </label>
          ))}
          {err && <div data-testid="cp-error" className="text-[12px] text-critical-red">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
                    data-testid="cp-cancel"
                    className="h-9 px-3 rounded-sm text-[12px] border border-coach-silver hover:bg-road-white">
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    data-testid="cp-submit"
                    className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-60">
              {busy ? "Saving…" : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
